import type { UserSummary } from './api';
import type { TileSlug } from '@/shared/modules/ids';
import { DEFAULT_MODULE_TREE } from '@/shared/modules/catalog';
import { deriveTileRules, type TileVisibilityRule } from '@/shared/modules/registries';
import { currentRegistries, isTileOn, isTilePageOn } from '@/shared/modules/store';

/**
 * Hub-tile visibility filter — TS mirror of PHP `hubVisibleTiles()` in
 * `hub/includes/tiles.php`. Each rule in this module corresponds to a
 * line in that PHP function (lines 156-190). Three flags compose the
 * gate:
 *
 *   - `owner_only`    requires an explicit grant in `allowed_tools`,
 *                     even admins are blocked without it.
 *   - `always_visible` overrides everything; every authed user sees it
 *                      regardless of role or tools (Preferences).
 *   - `allowed_roles` is the fallback when the user has zero
 *                     `allowed_tools` entries (role-default set).
 *
 * Tile slugs are the same strings used in the JWT `hub:tool` claim and
 * in `users.allowed_tools` — `cs-portal`, `res-tools`, `ai-ai-ai` (3-part),
 * etc. The frontend `ModuleSlug` (`coportal`, `restools`, `aiaiai`) is a
 * routing-only synonym; the visibility key is the canonical PHP tile slug.
 */
export type { TileSlug, TileVisibilityRule };

/**
 * Visibility table: each catalog module's `access` and `roles`
 * (`shared/modules/catalog.json`), the port of the `allowed_roles` /
 * `owner_only` / `always_visible` flags from PHP `hub/includes/tiles.php`,
 * plus `roleOnly` (Employee Hub: admin/manager/user regardless of
 * allowed_tools, never viewer/agent).
 */
export const TILE_VISIBILITY_RULES: Record<TileSlug, TileVisibilityRule> = deriveTileRules(DEFAULT_MODULE_TREE);

export function hasTool(slug: string, user: UserSummary | null): boolean {
  return !!user && (user.allowedTools?.includes(slug) ?? false);
}

// ── Page-grant encoding ──────────────────────────────────────────────
//
// `users.allowed_pages` is a flat `string[]`. Three token shapes live in
// it, and they are the ONLY three the whole stack recognises:
//
//   "hot-plate"        bare slug            → a CS/OPS Portal page (legacy).
//   "lemax:sops"       "{module}:{slug}"    → one page in a hub module.
//   "lemax:*"          "{module}:*"         → FULL ACCESS to that module,
//                                             including pages shipped later.
//   "lemax:!"          "{module}:!"         → explicit NO page in that
//                                             module (the deny sentinel).
//
// The wildcard is the canonical, materialised encoding of "full access".
// A module with *zero* entries also means full access ("inverted gate",
// PHP `hubUserCanAccessPage` rule 4) but that form is legacy-read-only:
// the admin matrix always writes the wildcard so the state is explicit,
// and so "full access" and "no access" can never render identically.
//
// The deny sentinel exists because zero-entries already means "full".
// Without it, unticking the last page in a module silently RESTORES full
// access — the exact privilege regression this encoding is here to kill.
export const PAGE_GRANT_WILDCARD = '*';
export const PAGE_GRANT_DENY = '!';

/**
 * Reserved namespace for CS/OPS Portal grant *tokens*. Portal pages
 * themselves stay bare (`"hot-plate"`), but "this user may see no portal
 * page at all" has no bare spelling, so it is written `"cs-portal:!"`.
 * The server sanitiser whitelists exactly this one namespaced token.
 */
export const COPORTAL_GRANT_NAMESPACE = 'cs-portal';
export const COPORTAL_DENY_ALL = `${COPORTAL_GRANT_NAMESPACE}:${PAGE_GRANT_DENY}`;

/**
 * EXPLICIT-ONLY page tokens — pages that implicit expansion never produces
 * (an unrestricted/empty bucket, a `{module}:*` wildcard, the role
 * fallback). Only an explicit grant opens them, and that grant arrives in
 * the server-expanded `allowedPages` from `users.extra_pages`. Mirrors the
 * `ExplicitOnly` flag in `PageRegistryController` (bare slug = CS/OPS
 * Portal, `{module}:{slug}` otherwise); `JwtTokenService.EffectivePages`
 * is the server twin. Keep the two in lockstep.
 */
export const EXPLICIT_ONLY_PAGE_TOKENS: ReadonlySet<string> = new Set([
  'hotline-whatsapp-my',
  'hotline-whatsapp-sg',
  // Reply grants (2026-09-24): extra_pages ONLY on the server, and only
  // meaningful next to the read page. They are rights, not rail pages.
  'hotline-whatsapp-my-write',
  'hotline-whatsapp-sg-write',
  // Petty Cash (2026-09-24): named people only — Syuaeib, JB, Management.
  'petty-cash',
]);

/** `true` when this token (bare portal slug or `{module}:{slug}`) is explicit-only. */
export function isExplicitOnlyToken(token: string): boolean {
  return EXPLICIT_ONLY_PAGE_TOKENS.has(token);
}

/** `true` for `"{module}:*"`. */
export function isWildcardGrant(entry: string, moduleSlug: string): boolean {
  return entry === `${moduleSlug}:${PAGE_GRANT_WILDCARD}`;
}

/** `true` for `"{module}:!"` — an explicit "no pages here" marker. */
export function isDenyGrant(entry: string, moduleSlug: string): boolean {
  return entry === `${moduleSlug}:${PAGE_GRANT_DENY}`;
}

/**
 * HR ▸ Appraisals reach gate. Mirror of the server `AppraisalAccess` tier
 * carried on `UserSummary.appraisalsAccess`. Returns true for full / team /
 * self (anyone who can open at least their own appraisal); false for 'none'
 * (viewer / agent) or a missing flag. The API still scopes every row
 * server-side — this only decides whether to surface the entry points.
 *
 * NOTE: the Appraisals subtree mounts at `/hr/appraisals` with its OWN guard
 * (`RequireAppraisalsAccess`), independent of the admin-only `hr` tile —
 * managers/employees reach it without seeing the other HR pages.
 */
export function canAccessAppraisals(user: UserSummary | null): boolean {
  if (!user) return false;
  const tier = user.appraisalsAccess;
  return tier === 'full' || tier === 'team' || tier === 'self';
}

export function hasRole(role: string, user: UserSummary | null): boolean {
  return !!user && user.role === role;
}

/**
 * TS mirror of PHP `hubUserHasModulePageGrant()` in
 * `hub/includes/page-registry.php`. Returns `true` iff the user has at
 * least one explicit per-page grant for this module in `allowedPages` —
 * i.e. any entry of the form `{module}:*` or `{module}:{slug}`.
 *
 * Used by `canSeeTile` (rule: a page-grant unlocks the parent tile even
 * without the tool) and by the per-page route gate, exactly as the PHP
 * module bootstrap does.
 *
 * The deny sentinel `{module}:!` is explicitly NOT a grant — it is the
 * opposite — so it must never unlock the tile.
 */
export function hasModulePageGrant(
  moduleSlug: string,
  user: UserSummary | null,
): boolean {
  if (!user) return false;
  return anyModulePageGrant(moduleSlug, user.allowedPages ?? []);
}

/** {@link hasModulePageGrant} on a bare token list. Shared with {@link tileVisibleFor}. */
function anyModulePageGrant(
  moduleSlug: string,
  allowedPages: readonly string[],
): boolean {
  const prefix = moduleSlug + ':';
  return allowedPages.some(
    (entry) =>
      typeof entry === 'string' &&
      entry.startsWith(prefix) &&
      !isDenyGrant(entry, moduleSlug),
  );
}

/**
 * The tile-unlock half of the page-grant rules, for ANY tile slug.
 *
 * {@link anyModulePageGrant} only understands `{module}:` tokens, which is
 * correct for every hub module — and wrong for exactly one tile. CS/OPS
 * Portal pages are stored BARE (`"issues"`, `"hot-plate"`; see the encoding
 * note above), so a `cs-portal` lookup through the namespaced predicate is
 * ALWAYS false. That made the client disagree with the server: the JWT
 * carries bare portal page claims (`JwtTokenService.EffectivePages` rule 1)
 * and `CurrentUser.CanAccess` honours them, while `<RequireTile
 * tile="cs-portal">` bounced the very same user to /forbidden. Reachable
 * straight from the rights matrix: untick Full access on CS/OPS Portal, keep
 * three pages, save.
 *
 * The portal bucket is the same one {@link canAccessCoportalPage} uses —
 * every bare token, plus anything under the reserved `cs-portal:` prefix —
 * minus the deny sentinel, which is the opposite of a grant and must never
 * unlock a tile.
 */
function anyTilePageGrant(
  tileSlug: string,
  allowedPages: readonly string[],
): boolean {
  if (tileSlug !== COPORTAL_GRANT_NAMESPACE)
    return anyModulePageGrant(tileSlug, allowedPages);
  const portalPrefix = `${COPORTAL_GRANT_NAMESPACE}:`;
  return allowedPages.some(
    (entry) =>
      typeof entry === 'string' &&
      entry.length > 0 &&
      entry !== COPORTAL_DENY_ALL &&
      (!entry.includes(':') || entry.startsWith(portalPrefix)),
  );
}

/**
 * TS mirror of PHP `hubUserCanAccessPage()` in
 * `hub/includes/page-registry.php`. Decides whether a user may view the
 * page `{moduleSlug}:{pageSlug}`. Rules, in order:
 *
 *   1. admin role → always pass.
 *   2. `freeAccess` page → always pass.
 *   3. `allowedPages` contains `{module}:*` or `{module}:{slug}` → pass.
 *   4. `allowedPages` has *no* entry prefixed `{module}:` → pass
 *      (backwards compat: tool/role access alone grants every page until
 *      an admin restricts it explicitly).
 *   5. otherwise → deny. `{module}:!` lands here by construction: it is a
 *      `{module}:` entry (so rule 4 no longer fires) that matches no page.
 *
 * NOTE on what `allowedPages` actually holds. Since the two auth response
 * shapes were unified, BOTH `/auth/login` and `/auth/me` return the
 * server-EXPANDED list (`JwtTokenService.EffectivePages`), never the raw
 * `users.allowed_pages` column. So rule 4 fires far less often than it
 * reads: a module at full access arrives here as one entry per page. Its
 * remaining job is modules the server does not model at all (unknown
 * namespaces such as `employeeportal:`), which is exactly why rule 5's
 * sentinel handling matters — the server emits `{module}:!` verbatim so a
 * REVOKED module cannot arrive looking like an unmodelled one.
 */
export function canAccessPage(
  moduleSlug: string,
  pageSlug: string,
  freeAccess: boolean,
  user: UserSummary | null,
): boolean {
  if (!user) return false;
  // A page the tenant switched off (Admin ▸ Modules) is shut for everyone,
  // admins included: checked before the admin rule (brief §2.3).
  if (!isTilePageOn(moduleSlug, pageSlug)) return false;
  if (user.role === 'admin') return true;
  if (freeAccess) return true;
  // A page can never be literally named `*` or `!` — the registry slugs
  // are `[a-z0-9-]+` — so the tokens can't be spoofed into a match.
  if (pageSlug === PAGE_GRANT_WILDCARD || pageSlug === PAGE_GRANT_DENY) return false;

  const prefix = moduleSlug + ':';
  // Explicit-only pages need their exact token: no wildcard, no
  // empty-bucket inheritance (see EXPLICIT_ONLY_PAGE_TOKENS).
  const explicitOnly = isExplicitOnlyToken(prefix + pageSlug);
  let hasModuleEntries = false;
  for (const entry of user.allowedPages ?? []) {
    if (typeof entry !== 'string') continue;
    if (!entry.startsWith(prefix)) continue;
    hasModuleEntries = true;
    if (entry === prefix + pageSlug) return true;
    if (entry === prefix + PAGE_GRANT_WILDCARD && !explicitOnly) return true;
  }
  return !hasModuleEntries && !explicitOnly;
}

/**
 * CS/OPS Portal sibling of {@link canAccessPage}. Portal pages are stored
 * BARE (`"hot-plate"`), not namespaced, so the bucket is "every bare token,
 * plus anything under the reserved `cs-portal:` prefix":
 *
 *   1. admin → pass.
 *   2. `freeAccess` page → pass.
 *   3. any bare entry present → pass iff the exact slug is among them. An
 *      explicit grant beats a stale `cs-portal:!` sitting beside it — the
 *      admin matrix renders exactly those boxes ticked, so anything else
 *      would make the UI lie.
 *   4. the bucket is empty → pass (inverted gate; the `cs-portal` tool tile
 *      is the module-level gate, pages are restrictions *within* it) —
 *      EXCEPT explicit-only pages, which need their exact slug.
 *   5. otherwise → deny. That covers `cs-portal:!` (explicit revoke-all)
 *      and any illegal `cs-portal:` token from a hand-edited row, which
 *      must read as "restricted", never fall through rule 4.
 *
 * Byte-for-byte the same decision tree as rule 1 of
 * `JwtTokenService.EffectivePages`, which is why the sentinel is emitted
 * verbatim by the server: `user.allowedPages` is the EXPANDED list, so a
 * revoked portal arrives here with an empty bare set and would otherwise
 * trip rule 4 and allow everything the server is about to 403.
 */
export function canAccessCoportalPage(
  pageSlug: string,
  freeAccess: boolean,
  user: UserSummary | null,
): boolean {
  if (!user) return false;
  if (!isTilePageOn(COPORTAL_GRANT_NAMESPACE, pageSlug)) return false;
  if (user.role === 'admin') return true;
  if (freeAccess) return true;
  if (pageSlug === PAGE_GRANT_WILDCARD || pageSlug === PAGE_GRANT_DENY) return false;

  const entries = (user.allowedPages ?? []).filter(
    (e): e is string => typeof e === 'string' && e.length > 0,
  );
  const portalPrefix = `${COPORTAL_GRANT_NAMESPACE}:`;
  const bucket = entries.filter(
    (e) => !e.includes(':') || e.startsWith(portalPrefix),
  );

  const bare = bucket.filter((e) => !e.includes(':'));
  if (bare.length > 0) return bare.includes(pageSlug);
  // An empty bucket is the whole portal — minus the explicit-only pages,
  // which only an exact grant (users.extra_pages) opens.
  return bucket.length === 0 && !isExplicitOnlyToken(pageSlug);
}

/**
 * Canonical visibility predicate. Returns `true` iff the user should
 * see the tile on the dashboard / in the rail. Mirrors PHP rules
 * 1-4 of `hubVisibleTiles()`:
 *
 *   0. the tenant switched the module off (Admin ▸ Modules) → false, for
 *      admins too (brief §2.3).
 *   1. unknown slug → false (defensive; never silently allow).
 *   2. always_visible → true regardless of role/tools.
 *   3. owner_only → requires explicit `allowed_tools` grant for the
 *      EXACT same slug (admin bypass does NOT apply) OR a per-page grant
 *      (`{slug}:*` / `{slug}:<page>`) for the same module.
 *   4. admin role → see every non-owner-only tile.
 *   5. user with non-empty `allowed_tools` → see tiles present in
 *      that list (plus always_visible, plus per-page grant).
 *   6. user with empty `allowed_tools` → fall back to `allowed_roles`
 *      (plus per-page grant).
 *
 * The per-page-grant unlock mirrors PHP `hubVisibleTiles()`'s
 * `$hasPageGrant($slug)` term in `ownerGate` and in both the tool-list
 * and role-fallback filters: a user with only `{module}:<page>` in
 * `allowed_pages` still sees the tile so the page stays reachable.
 */
export function canSeeTile(
  slug: string,
  user: UserSummary | null,
): boolean {
  if (!user) return false;
  return tileVisibleFor(slug, {
    role: user.role,
    allowedTools: user.allowedTools ?? [],
    allowedPages: user.allowedPages ?? [],
    canEditTariff: user.canEditTariff === true,
  });
}

/**
 * The raw rules behind {@link canSeeTile}, on plain fields instead of a
 * `UserSummary`.
 *
 * The admin rights matrix needs exactly this: it edits `allowedTools` /
 * `allowedPages` as LOCAL working state and must answer "would this tile
 * still be reachable after the admin's unsaved edits?" — for a user who is
 * not the one signed in. Feeding a synthetic summary through `canSeeTile`
 * would work but invites someone to widen `UserSummary` and break the
 * matrix; this signature says what the decision actually depends on.
 *
 * Server twin of the tool/role half: `EffectivePages`'s `Reachable()` plus
 * `ToolsRegistryController.TileFallbackRoles`.
 */
export interface TileGrantState {
  role: string;
  allowedTools: readonly string[];
  allowedPages: readonly string[];
  canEditTariff?: boolean;
}

export function tileVisibleFor(slug: string, state: TileGrantState): boolean {
  // The tenant's rules (`access` can be overridden in the module config).
  const rule = (currentRegistries().tileRules as Record<string, TileVisibilityRule>)[slug];
  if (!rule) return false;

  // Rule 0 — a module the tenant switched off (Admin ▸ Modules) is shut for
  // EVERYONE. It must come before rule 4 (admin sees everything), or the
  // demo's admin would still see Lemax (brief §2.3).
  if (!isTileOn(slug)) return false;

  // Rule 2 — always_visible short-circuit. Preferences is the only
  // current example; every authed user sees it.
  if (rule.alwaysVisible) return true;

  // Data ▸ Tariff team grant — a Data-team member (server-computed
  // `canEditTariff`) sees the `data` tile without a per-user tool/role grant.
  // Mirrors the server IsTariffEditor gate on the tariff API.
  if (slug === 'data' && state.canEditTariff === true) return true;

  const pageGrant = anyTilePageGrant(slug, state.allowedPages);

  // Rule 3 — owner_only requires the literal tool grant OR a per-page
  // grant. Admin does NOT bypass this. AI AI AI is the only current example.
  if (rule.ownerOnly) {
    return state.allowedTools.includes(slug) || pageGrant;
  }

  // roleOnly — judged purely on the role list, ignoring allowed_tools, so a
  // tool-granted manager/user still sees it but viewer/agent never do.
  if (rule.roleOnly) {
    return rule.allowedRoles.includes(state.role);
  }

  // Rule 4 — admin sees everything else.
  if (state.role === 'admin') return true;

  // Rule 5 — when the user has any explicit tool grants, those are
  // the canonical visibility set. Role no longer matters, but a per-page
  // grant still unlocks the tile.
  if (state.allowedTools.length > 0) {
    return state.allowedTools.includes(slug) || pageGrant;
  }

  // Rule 6 — tool-less user: fall back to allowed_roles, plus per-page grant.
  return rule.allowedRoles.includes(state.role) || pageGrant;
}
